import { Stack, TextField, Typography } from '@mui/material';
import {
  LANDING_PAGE_LOGO_HEIGHT_MAX,
  LANDING_PAGE_LOGO_HEIGHT_MIN,
} from '@provision/landing-page-utils';
import { Controller, useFormContext } from 'react-hook-form';
import { ColorPickerField } from 'src/pages/Settings/memberships/components/color-picker-field';
import { PixelSliderField } from 'src/pages/Settings/memberships/components/landing-page-tab/pixel-slider-field';
import { type LandingPageFormValues } from 'src/pages/Settings/memberships/components/landing-page-tab/types';
import { panelFieldLabelSx } from 'src/pages/Settings/memberships/styles';
import { COLOR_FIELD_RULES } from 'src/pages/Settings/memberships/utils/color-field-rules';
import { URL_FIELD_RULES } from 'src/pages/Settings/memberships/utils/url-field-rules';

const HEADER_NOTE =
  'Leave a color empty to keep the default. Text picks light or dark on its own unless you set it.';

const LOGO_PLACEHOLDER = 'https://example.com/logo.png';

interface HeaderSettingsFieldsProps {
  readOnly: boolean;
  /** The client segment's logo, which the page shows while its own Logo URL is empty. */
  segmentLogoUrl?: string | null;
}

// Says where the header's logo comes from, since an empty field still shows the segment's.
function logoUrlHint(value: string, segmentLogoUrl: string | null | undefined): string {
  if (value.trim()) {
    return 'Only this landing page uses it.';
  }
  if (segmentLogoUrl) {
    return "Using the client segment's logo. Enter a URL to use a different one here.";
  }
  return 'Empty, so the header shows the segment name.';
}

export const HeaderSettingsFields = ({
  readOnly,
  segmentLogoUrl,
}: HeaderSettingsFieldsProps) => {
  const { control } = useFormContext<LandingPageFormValues>();

  return (
    <Stack gap={1}>
      <Typography variant="caption" sx={panelFieldLabelSx}>
        Header
      </Typography>

      <Controller
        name="settings.logoUrl"
        control={control}
        rules={URL_FIELD_RULES}
        render={({ field, fieldState }) => (
          <TextField
            {...field}
            label="Logo URL"
            size="small"
            fullWidth
            disabled={readOnly}
            placeholder={segmentLogoUrl || LOGO_PLACEHOLDER}
            // Kept up so the placeholder, the segment's logo when there is one, always shows.
            slotProps={{ inputLabel: { shrink: true } }}
            error={Boolean(fieldState.error)}
            helperText={fieldState.error?.message ?? logoUrlHint(field.value, segmentLogoUrl)}
          />
        )}
      />

      <PixelSliderField
        name="settings.logoHeight"
        label="Logo height"
        min={LANDING_PAGE_LOGO_HEIGHT_MIN}
        max={LANDING_PAGE_LOGO_HEIGHT_MAX}
        readOnly={readOnly}
      />

      <Stack sx={{ '& > *': { borderBottom: 1, borderColor: 'divider' } }}>
        <Controller
          name="settings.headerBackgroundColor"
          control={control}
          rules={COLOR_FIELD_RULES}
          render={({ field, fieldState }) => (
            <ColorPickerField
              field={field}
              label="Background"
              readOnly={readOnly}
              hasError={Boolean(fieldState.error)}
            />
          )}
        />
        <Controller
          name="settings.headerTextColor"
          control={control}
          rules={COLOR_FIELD_RULES}
          render={({ field, fieldState }) => (
            <ColorPickerField
              field={field}
              label="Text"
              readOnly={readOnly}
              hasError={Boolean(fieldState.error)}
            />
          )}
        />
      </Stack>

      <Typography variant="body2" color="text.secondary">
        {HEADER_NOTE}
      </Typography>
    </Stack>
  );
};
